import { omit } from "lodash"
import config from "react-global-configuration"
import { all, call, put, race, select, take, takeLatest } from "redux-saga/effects"

import clientActions from "client/services/actions"
import { RESET_STORE, SET_CUSTOMER } from "client/services/constants"
import { getCustomer, getNirReaderResponse } from "client/services/selector"
import { insertRelativesRequest } from "containers/InsertRelative/services/actions"
import profileActions from "containers/Profile/services/actions"
import signupActions from "containers/Signup/services/actions"
import { signupApi } from "containers/Signup/services/saga"
import { request } from "lib/request"
import { Customer, Relative } from "types/entity"
import { CustomerApi, ResponseVerifyPhone } from "types/payload"
import { Action, GFlow, GWatcher, Message } from "types/redux"
import { Patient } from "types/sesam"
import { GlobalStore, NirReaderResponse } from "types/store"

import actions from "./actions"
import {
  CANCEL_SIGNUP_REQUEST,
  CHANGE_PHONE_REQUEST,
  SEND_AGAIN_REQUEST,
  SET_PHONE_CODE_REQUEST,
} from "./constants"
import { languages } from "locales/languages"

/*
 *  API REQUESTS
 */
async function verifyPhoneApi(payload: {
  code: string
}): Promise<ResponseVerifyPhone> {
  return await request(config.get("patient.verify"), {
    method: "POST",
    payload,
  })
}
async function cancelApi(): Promise<ResponseVerifyPhone> {
  return await request(config.get("patient.cancelSignup"), {
    method: "POST",
    payload: {},
  })
}
async function sendAgainApi(): Promise<ResponseVerifyPhone> {
  return await request(config.get("patient.verifySendAgain"), {
    method: "POST",
    payload: {},
  })
}
/*
 * FLOWS
 */
function* sendAgainFlow() {
  try {
    yield call(sendAgainApi)
    yield put(actions.sendAgainSuccess(languages.verifyCallSendSuccess))
  } catch (e) {
    console.error(e, {
      route: config.get("patient.verifySendAgain")
    })
    yield put(actions.sendAgainError(languages.cannotSendTheSms))
  }
}
function* cancelFlow() {
  try {
    yield call(cancelApi)
    yield put(signupActions.reset())
  } catch (e) {
    console.error(e, {
      route: config.get("patient.cancelSignup")
    })
    yield put(actions.cancelError(languages.cannotCancel))
  }
}

// Signing up again with the new phone links the installation to it and sends a new code
function* changePhoneFlow({ payload: phone }: Action<string>) {
  const signupPayload: CustomerApi = yield select(
    ({ verifyPhone }: GlobalStore) => verifyPhone.signupPayload
  )
  const customer: Customer = yield select(getCustomer)
  try {
    // An answer arriving after the signup was cancelled must not restore it
    const { cancelled } = yield race({
      response: call(signupApi, { ...signupPayload, phone }),
      cancelled: take([CANCEL_SIGNUP_REQUEST, RESET_STORE]),
    })
    if (cancelled) return
    yield all([
      put(actions.setSignupPayload({ ...signupPayload, phone })),
      put({ type: SET_CUSTOMER, payload: { ...customer, phone } }),
      put(actions.changePhoneSuccess(languages.weHaveSendAnSms)),
    ])
  } catch (e) {
    console.error(e, {
      route: config.get("patient.signup")
    })
    yield put(actions.changePhoneError((e as string) || languages.cannotSendTheSms))
  }
}

function* verifyPhoneFlow({
  payload,
}: Action<{ code: string }>): GFlow<Action<Message>> {
  try {
    const response: ResponseVerifyPhone = yield call(verifyPhoneApi, payload)
    if (response && response.customer) {
      // Get NirReader patients
      const nirReaderResponse: NirReaderResponse | undefined = yield select(
        getNirReaderResponse
      )
      const { patient, relatives } = nirReaderResponse ?? {
        patient: null,
        relatives: null,
      }
      // Add Nir after a success signup + verified user
      const nir = patient?.nir
      if (nir) yield put(profileActions.changePatientRequest({ nir, signup_by_carte_vitale:true }))
      // Add Relatives in Nir if they exists
      if (relatives) {
        const relativesFormat: Relative[] = relatives.map(
          (r: Partial<Patient>) => {
            return {...omit(r, ["amc", "cmu", "amo"]), signup_by_carte_vitale:true } as unknown as Relative
          }
        )
        yield put(insertRelativesRequest(relativesFormat))
      }
      yield all([
        put(clientActions.setCustomer(response.customer)),
        put(actions.success(languages.SignupSuccess)),
      ])
    } else {
      yield put(
        actions.error(languages.RefraichToUpdateInfo)
      )
    }
  } catch (error) {
    console.error(error, {
      route: config.get("patient.verify")
    })
    yield put(actions.error((error as string) || languages.WrongCode))
  }
}

function* signupWatcher(): GWatcher {
  yield takeLatest(SET_PHONE_CODE_REQUEST, verifyPhoneFlow)
  yield takeLatest(SEND_AGAIN_REQUEST, sendAgainFlow)
  yield takeLatest(CANCEL_SIGNUP_REQUEST, cancelFlow)
  yield takeLatest(CHANGE_PHONE_REQUEST, changePhoneFlow)
}

export default signupWatcher
